import { Users, DollarSign, Target, ChevronRight, ExternalLink, Award, Plus } from 'lucide-react';
import { cn } from '../../../utils/helpers';

const campaigns = [
  { id: 1, name: 'Messego Pro Launch', commission: '25%', status: 'Active', clicks: '1,240', conversions: '45', earnings: '$1,125.00' },
  { id: 2, name: 'Marketplace Referral', commission: '5%', status: 'Active', clicks: '850', conversions: '12', earnings: '$450.00' },
  { id: 3, name: 'Career Services', commission: '15%', status: 'Paused', clicks: '320', conversions: '8', earnings: '$240.00' },
];

export default function Affiliate() {
  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Affiliate Dashboard</h1>
          <p className="text-neutral-500 dark:text-neutral-400">Track your referrals, campaigns, and earnings.</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20">
          <Plus className="w-4 h-4" />
          New Campaign
        </button>
      </header>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Total Earnings', value: '$1,815.00', icon: DollarSign, color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40' },
          { label: 'Active Referrals', value: '156', icon: Users, color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' },
          { label: 'Conversion Rate', value: '3.8%', icon: Target, color: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/40' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-neutral-900 p-8 rounded-[2rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none">
            <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center mb-4", stat.color)}>
              <stat.icon className="w-6 h-6" />
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 font-bold uppercase tracking-widest">{stat.label}</p>
            <h3 className="text-3xl font-bold mt-1">{stat.value}</h3>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Campaigns Table */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Active Campaigns</h2>
            <button className="text-sm font-bold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">View all</button>
          </div>
          <div className="bg-white dark:bg-neutral-900 rounded-[2rem] border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm dark:shadow-none">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-neutral-50">
                    <th className="px-6 py-4 text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">Campaign</th>
                    <th className="px-6 py-4 text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">Clicks</th>
                    <th className="px-6 py-4 text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">Earnings</th>
                    <th className="px-6 py-4 text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {campaigns.map((camp) => (
                    <tr key={camp.id} className="hover:bg-neutral-50/50 transition-colors group cursor-pointer">
                      <td className="px-6 py-4">
                        <p className="font-bold text-sm">{camp.name}</p>
                        <p className="text-[10px] text-neutral-500 dark:text-neutral-400">{camp.commission} Commission</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-bold">{camp.clicks}</p>
                        <p className="text-[10px] text-neutral-400 dark:text-neutral-500">{camp.conversions} conv.</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{camp.earnings}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={cn(
                          "text-[10px] font-bold px-2 py-1 rounded-lg uppercase tracking-wider",
                          camp.status === 'Active' ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400" : "bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500"
                        )}>
                          {camp.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <ChevronRight className="w-4 h-4 text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-50 transition-colors inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Affiliate Sidebar */}
        <div className="space-y-6">
          <div className="bg-neutral-900 rounded-[2.5rem] p-8 text-white">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
              <Award className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">Top Performer</h3>
            <p className="text-neutral-400 dark:text-neutral-500 text-xs mb-6">You're in the top 5% of affiliates this month. Keep it up!</p>
            <div className="space-y-4">
              <div className="flex justify-between text-xs font-bold">
                <span>Next Reward</span>
                <span>$2,500</span>
              </div>
              <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden">
                <div className="bg-amber-400 w-[72%] h-full rounded-full shadow-[0_0_10px_rgba(251,191,36,0.5)]"></div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-900 p-8 rounded-[2.5rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none">
            <h3 className="font-bold mb-4">Your Referral Link</h3>
            <div className="flex items-center gap-2 bg-neutral-50 dark:bg-neutral-950 p-3 rounded-xl border border-neutral-100 dark:border-neutral-800">
              <p className="text-xs font-mono text-neutral-500 dark:text-neutral-400 truncate">messego.com/ref/alex_j</p>
              <button className="p-2 rounded-lg hover:bg-white dark:hover:bg-neutral-900 transition-all text-neutral-900 dark:text-neutral-50">
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-4 text-center">Share this link to earn commissions on every sign-up.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
